import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Play, Star } from "lucide-react";
import { useCheckout } from "@/lib/checkout";

export const Hero = () => {
  const { startCheckout, loadingPlan } = useCheckout();
  return (
    <section
      id="top"
      className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Ambient glows */}
      <div
        aria-hidden
        className="absolute -top-32 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-blue-600/20 blur-[140px]"
      />
      <div
        aria-hidden
        className="absolute top-40 -right-24 h-[420px] w-[420px] rounded-full bg-purple-600/20 blur-[140px]"
      />
      <div aria-hidden className="absolute inset-0 grid-bg" />
      <div aria-hidden className="absolute inset-0 noise" />

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-zinc-300"
          data-testid="hero-badge"
        >
          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          New · Powered by GPT-grade resume engine
          <Star className="h-3 w-3 text-zinc-500" />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-white text-balance"
          data-testid="hero-headline"
        >
          Get More Interviews With an{" "}
          <span className="relative inline-block">
            <span className="bg-gradient-to-r from-blue-400 via-indigo-300 to-purple-400 bg-clip-text text-transparent">
              AI-Generated
            </span>
          </span>{" "}
          Resume
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-6 max-w-2xl mx-auto text-base sm:text-lg text-zinc-400 leading-relaxed text-balance"
          data-testid="hero-subheadline"
        >
          Receive a modern, optimized, and professional resume — analyzed,
          rewritten, and delivered by AI in minutes.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <button
            onClick={() => startCheckout("cv_pro")}
            disabled={loadingPlan === "cv_pro"}
            className="group inline-flex items-center gap-2 rounded-full bg-white text-black px-7 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors shadow-[0_8px_30px_rgba(255,255,255,0.12)] disabled:opacity-60"
            data-testid="hero-cta-primary"
          >
            {loadingPlan === "cv_pro" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            Get My Resume
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          <a
            href="#examples"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3 text-sm font-medium text-white hover:bg-white/10 transition-colors"
            data-testid="hero-cta-secondary"
          >
            <Play className="h-3.5 w-3.5" />
            See Examples
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="mt-12 flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-xs text-zinc-500"
          data-testid="hero-trust-row"
        >
          <span>ATS-Optimized</span>
          <span className="h-1 w-1 rounded-full bg-zinc-700" />
          <span>PDF Delivery</span>
          <span className="h-1 w-1 rounded-full bg-zinc-700" />
          <span>Modern Templates</span>
          <span className="h-1 w-1 rounded-full bg-zinc-700" />
          <span>Delivered in minutes</span>
        </motion.div>
      </div>
    </section>
  );
};
